import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

const control =
  'block w-full rounded-lg border bg-white px-3 text-sm text-ink-900 shadow-xs placeholder:text-ink-400 disabled:bg-ink-50 disabled:text-ink-500';
const ok =
  'border-ink-200 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20';
const bad =
  'border-status-exception focus:outline-none focus:ring-2 focus:ring-status-exception/20';

interface FieldShellProps {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/** Label + control + hint/error, wired up with ids for screen readers. */
export function FieldShell({ label, error, hint, children, className = '' }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-800">
        {label}
      </label>
      {children({
        id,
        describedBy: [hintId, errorId].filter(Boolean).join(' ') || undefined,
        invalid: Boolean(error),
      })}
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs text-ink-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1.5 text-xs font-medium text-status-exception">
          {error}
        </p>
      )}
    </div>
  );
}

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: ReactNode;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, className, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} error={error} hint={hint} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          ref={ref}
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`${control} h-10 ${invalid ? bad : ok}`}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string;
  hint?: ReactNode;
};

export function SelectField({
  label,
  error,
  hint,
  className,
  children,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell label={label} error={error} hint={hint} className={className}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`${control} h-10 pr-8 ${invalid ? bad : ok}`}
          {...rest}
        >
          {children}
        </select>
      )}
    </FieldShell>
  );
}

type TextAreaFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string;
  hint?: ReactNode;
};

export function TextAreaField({ label, error, hint, className, ...rest }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} error={error} hint={hint} className={className}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`${control} py-2 ${invalid ? bad : ok}`}
          rows={3}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

/** Inline search box used above tables. */
export function SearchInput(props: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const { label, className = '', ...rest } = props;
  return (
    <div className={`relative ${className}`}>
      <svg
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M9 3.5a5.5 5.5 0 1 0 3.3 9.9l3.15 3.15a.75.75 0 1 0 1.06-1.06l-3.15-3.15A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
          clipRule="evenodd"
        />
      </svg>
      <input type="search" aria-label={label} className={`${control} ${ok} h-10 pl-9`} {...rest} />
    </div>
  );
}
